---
title: "Как сохранить Excel в PDF, чтобы таблица не обрезалась"
description: "Excel режет последние колонки при экспорте в PDF? Вот как поймать масштаб, альбомную ориентацию и область печати без ручной возни с настройками."
locale: "ru"
date: "2026-05-18"
tags: ["excel", "pdf", "таблицы"]
relatedTools: ["excelVPdf"]
translations:
  en: "how-to-save-excel-as-pdf-without-cutting-off-columns"
faq:
  - q: "Почему Excel обрезает таблицу при сохранении в PDF?"
    a: "Потому что PDF собирается из печатного макета. Если лист шире выбранной бумаги, Excel переносит лишние колонки на следующую страницу или режет область печати. Чаще всего виноваты масштаб, портретная ориентация или старая print area."
  - q: "Можно ли уместить все колонки на одной странице?"
    a: "Да, если таблица не слишком широкая. В Excel это настройка `Fit to 1 page wide`; конвертер ниже сам вписывает все колонки в ширину страницы. Но 28 колонок с длинными заголовками на A4 всё равно станут мелким серым ковром."
  - q: "Файл обрабатывается локально или отправляется на сервер?"
    a: "Локально. Таблица читается и собирается в PDF прямо в браузере, файл не уходит на сервер. Оформление при этом не сохраняется: переносятся значения ячеек."
  - q: "Что проверить перед отправкой PDF?"
    a: "Откройте итоговый PDF и посмотрите первую и последнюю страницы: видны ли крайние колонки, не появилась ли пустая страница, читаются ли заголовки. Для отчётов это быстрее, чем потом объяснять, куда пропал столбец `Сумма`."
---

import { ExcelVPdfForm } from '@/pages/excel-v-pdf/ExcelVPdfForm'

`otchot_aprel.xlsx`: 17 колонок, две вкладки, справа важный столбец
`Сумма`. В Excel всё выглядит нормально. Сохраняешь в PDF, отправляешь
руководителю, а там первые 13 колонок на первой странице, последние 4 -
на второй. Формально файл не сломан. Практически его читать невозможно.

Ниже встроен конвертер `Excel → PDF`. Он собирает PDF прямо в браузере:
файл никуда не уходит, все колонки вписываются в ширину альбомного A4.
Цвета и объединённые ячейки при этом не сохраняются. Без регистрации и
водяных знаков.

<div className="my-8 rounded-xl border bg-card p-5 shadow-sm">
  <ExcelVPdfForm />
</div>

## Почему Excel режет таблицу

Excel не думает страницами, пока вы работаете с таблицей. У него
бесконечная сетка, где можно положить 200 колонок вправо и забыть, что
бумага вообще существует. PDF, наоборот, живёт страницами: A4, A3,
портрет, альбом, поля, масштаб. В момент экспорта Excel пытается
запихнуть сетку в печатный макет.

Самая частая ошибка - сохранить файл как есть, не открывая preview.
Портретный A4 принимает примерно 7-10 обычных колонок. Альбомный A4 -
уже больше, но не бесконечно. Если в таблице есть длинные заголовки
вроде `Комментарий менеджера по просроченной оплате`, ширина улетает
быстрее, чем кажется.

Microsoft в своих инструкциях называет это `Scale to Fit`: можно
сказать Excel уместить лист в одну страницу по ширине. Хорошая
настройка, но с подвохом. Когда таблица широкая, Excel просто уменьшает
масштаб. Колонки остаются на одной странице, зато текст превращается в
пыль. Для отчёта на 12 колонок это нормально. Для выгрузки из CRM на 38
колонок - уже самообман.

## Где обычно чинится

Начните не с конвертера, а с preview. Если таблица шире, чем выше, ей
почти всегда нужен landscape. Это звучит слишком просто, но половина
испорченных PDF из Excel начинается именно с портретного A4, который
молча делит отчёт на две страницы по горизонтали.

Потом посмотрите на область печати. Старые Excel-файлы любят тащить за
собой print area из прошлой жизни: кто-то когда-то выделил `A1:M42`,
потом добавил ещё четыре колонки, а PDF продолжил печатать старую рамку.
Снаружи это выглядит как баг конвертера, хотя проблема лежит прямо в
файле.

Ещё один неприятный случай - пустые хвосты. Excel считает использованной
областью не только видимые данные, но и форматирование. Если кто-то
залил цветом весь столбец `XFD`, экспорт может внезапно получить лишние
пустые страницы. Такое лечится не магией, а удалением лишних строк и
колонок.

## Когда онлайн-конвертер удобнее

Smallpdf, iLovePDF и похожие сервисы давно закрывают общий запрос
`Excel to PDF`: загрузил файл, скачал результат. Это удобно, когда под
рукой нет Excel или файл прилетел на чужом ноутбуке. Но у таких страниц
обычно нет честного разговора про то, почему таблицу разорвало на
страницы. Кнопка есть, диагностики нет.

Здесь логика чуть приземлённее. Таблица собирается локально: SheetJS
читает книгу, pdf-lib собирает PDF, файл не уходит наружу. Цену за это
видно сразу: оформление не переносится, а страницы получаются картинками.
Если таблица нарядная и важен точный вид, сохраните PDF в самом Excel по
советам выше.

Сверхширокую таблицу всё равно никто красиво не спасёт одной кнопкой.
Если у вас 31 колонка, мелкий шрифт 8 pt и пять уровней заголовков,
нормальный PDF получится только после решения: убрать лишнее, разнести
по листам, перейти на A3 или оставить Excel как исходный формат. PDF -
это финальный снимок, а не резиновая таблица.

После конвертации откройте результат и посмотрите на правый край. Если
там виден `Сумма`, нет пустой страницы в конце и заголовки читаются без
зумирования до 180%, файл можно отправлять. Если нет - проблема уже не в
формате PDF, а в том, что таблица просит человеческой верстки.
